/**
 * Numerical Expressions with Exponents generator (6.PAFR.2.3).
 *
 * Evaluate numerical expressions containing a whole-number exponent, applying
 * order of operations. The power is computed with the exact-rational `power`
 * helper and the surrounding arithmetic is done on Rationals, so the answer is
 * derived from the same values shown in the prompt. Bases stay small (2-6) and
 * exponents small (2-3) so answers remain reasonable for 6th grade.
 *
 * Families:
 *   F1: b^e                     (single power)
 *   F2: b^e + k  or  b^e - k    (power plus/minus a number)
 *   F3: b^e * k                 (power times a number)
 *   F4: (b + c)^e               (power with grouping)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { power } from "../engine/expression";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "exponent-expressions" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly baseMax: number;
  readonly expMax: number;
  readonly addMax: number;
  readonly factorMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], baseMax: 5, expMax: 2, addMax: 9, factorMax: 4 },
  standard: { families: ["F1", "F2", "F3"], baseMax: 6, expMax: 3, addMax: 12, factorMax: 5 },
  hard: { families: ["F2", "F3", "F4"], baseMax: 6, expMax: 3, addMax: 15, factorMax: 6 },
  challenge: { families: ["F3", "F4"], baseMax: 6, expMax: 3, addMax: 20, factorMax: 8 },
};

export class ExponentExpressionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      const base = rat(rng.int(2, cfg.baseMax));
      const exp = rng.int(2, cfg.expMax);
      const powered = power(base, exp);

      let answer: Rational;
      let promptText: string;
      let promptLatex: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          answer = powered;
          promptText = `Evaluate ${base.toString()}^${exp}.`;
          promptLatex = `${base.toLatex()}^{${exp}}`;
          steps = [
            {
              description: `The exponent means multiply the base ${exp} times.`,
              latex: `${base.toLatex()}^{${exp}} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F2": {
          const k = rat(rng.int(1, cfg.addMax));
          const subtract = rng.bool(0.5) && powered.gt(k);
          answer = subtract ? powered.sub(k) : powered.add(k);
          const opSym = subtract ? "-" : "+";
          promptText = `Evaluate ${base.toString()}^${exp} ${opSym} ${k.toString()}.`;
          promptLatex = `${base.toLatex()}^{${exp}} ${opSym} ${k.toLatex()}`;
          steps = [
            {
              description: "Evaluate the power first (order of operations).",
              latex: `${powered.toLatex()} ${opSym} ${k.toLatex()}`,
            },
            {
              description: subtract ? "Then subtract." : "Then add.",
              latex: `= ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F3": {
          const k = rat(rng.int(2, cfg.factorMax));
          answer = powered.mul(k);
          promptText = `Evaluate ${base.toString()}^${exp} * ${k.toString()}.`;
          promptLatex = `${base.toLatex()}^{${exp}} \\times ${k.toLatex()}`;
          steps = [
            {
              description: "Evaluate the power first (order of operations).",
              latex: `${powered.toLatex()} \\times ${k.toLatex()}`,
            },
            { description: "Then multiply.", latex: `= ${answer.toLatex()}` },
          ];
          break;
        }
        case "F4": {
          // (b + c)^e — keep the inner sum small so the answer stays reasonable.
          const b = rng.int(1, 3);
          const c = rng.int(1, 3);
          const sum = rat(b + c);
          answer = power(sum, exp);
          promptText = `Evaluate (${b} + ${c})^${exp}.`;
          promptLatex = `(${b} + ${c})^{${exp}}`;
          steps = [
            {
              description: "Simplify inside the parentheses first.",
              latex: `(${sum.toLatex()})^{${exp}}`,
            },
            {
              description: "Then apply the exponent.",
              latex: `= ${answer.toLatex()}`,
            },
          ];
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: `${promptLatex} = \\;?` },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "An exponent tells you how many times to multiply the base by itself." },
          { level: 2, text: "Order of operations: grouping, then exponents, then multiply/divide, then add/subtract." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const exponentExpressions = new ExponentExpressionsGenerator();
